import React from 'react';
import { 
  Alert, 
  AlertTitle, 
  alpha,
  Box, 
  Button, 
  Chip, 
  CircularProgress, 
  Container, 
  Divider, 
  LinearProgress,
  Paper, 
  SxProps, 
  Theme, 
  Typography, 
  useTheme, 
} from '@mui/material';
import { useAnalysis } from '../context/AnalysisContext';
import { AnalysisResult, CategoryScore } from '../types/analysis';
import { getScoreLabel } from '../utils/scoreUtils';

type ScoreBarColor = 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info';

interface ScoreBarProps {
  score: number;
  max?: number;
  height?: number;
  showScore?: boolean;
  color?: ScoreBarColor;
  sx?: SxProps<Theme>;
}

const getScoreColor = (value: number, type: 'score' | 'percentage' = 'score', max: number = 10): ScoreBarColor => {
  const percentage = type === 'percentage' ? value : (value / max) * 100;
  
  if (percentage < 40) return 'error';
  if (percentage < 70) return 'warning';
  if (percentage < 85) return 'info';
  return 'success';
};

const formatCategoryName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface CategoryCardProps {
  category: string;
  data: CategoryScore;
  index: number;
}

interface ResultDisplayProps {}

const ScoreBar: React.FC<ScoreBarProps> = (props) => {
  const { 
    score, 
    max = 10, 
    height = 8, 
    showScore = true,
    color: colorProp,
    sx
  } = props;
  const theme = useTheme();
  const percentage = Math.min(100, (score / max) * 100);
  
  // Determine color if not provided
  const scoreColor: ScoreBarColor = colorProp || getScoreColor(percentage, 'percentage');
  
  // Type-safe way to access theme colors
  const getScoreColorValue = (): string => {
    const colorMap: Record<ScoreBarColor, string> = {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      success: theme.palette.success.main,
    };
    return colorMap[scoreColor as ScoreBarColor];
  };
  
  const boxSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    ...sx
  };

  return (
    <Box sx={boxSx}>
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <LinearProgress 
          variant="determinate" 
          value={100}
          sx={{
            height,
            borderRadius: height / 2,
            backgroundColor: alpha(theme.palette.grey[300], 0.3),
            position: 'absolute',
            width: '100%',
          }}
        />
        <LinearProgress 
          variant="determinate" 
          value={percentage}
          color={scoreColor as any} // Type assertion needed for MUI LinearProgress
          sx={{
            height,
            borderRadius: height / 2,
            backgroundColor: 'transparent',
            '& .MuiLinearProgress-bar': {
              borderRadius: height / 2,
              transition: 'transform 0.3s ease',
            },
          }}
        />
      </Box>
      {showScore && (
        <Box sx={{ minWidth: 60, textAlign: 'right' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: getScoreColorValue(),
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap'
            }}
          >
            {score.toFixed(1)}/{max}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface ResultDisplayProps {
  /** Render this result instead of the one from context (used by the sample report page) */
  result?: AnalysisResult;
  /** Sample mode: hides Clear Results and shows a CTA to analyze your own deck */
  sample?: boolean;
}

export const ResultDisplay: React.FC<ResultDisplayProps> = ({ result: resultProp, sample = false }) => {
  const { result: contextResult, clearResult } = useAnalysis();
  const result = resultProp ?? contextResult;
  const [parsedResult, setParsedResult] = React.useState<AnalysisResult | null>(null);
  const [isLoadingState, setIsLoadingState] = React.useState(true);
  const [errorState, setErrorState] = React.useState<string | null>(null);

  // Process the result when it changes
  React.useEffect(() => {
    const processData = async () => {
      if (!result) {
        setIsLoadingState(false);
        return;
      }
      
      try {
        // Process category scores to ensure they have the correct structure
        const processedCategoryScores: Record<string, CategoryScore> = {};
        
        if (result.category_scores) {
          Object.entries(result.category_scores).forEach(([category, score]) => {
            if (score === undefined || score === null) return;
            
            // Helper to create a human-readable display name
            const defaultDisplayName = category
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            if (typeof score === 'number') {
              // If full_analysis has detailed info, prefer that
              const detailed = (result as any).full_analysis?.category_scores?.[category];
              if (detailed && typeof detailed === 'object') {
                const d: any = detailed;
                processedCategoryScores[category] = {
                  score: d.score ?? score,
                  maxScore: d.maxScore ?? d.max_score ?? 10,
                  displayName: d.displayName ?? d.display_name ?? defaultDisplayName,
                  reasoning: d.reasoning ?? d.analysis ?? d.comment ?? d.notes ?? '',
                  originalScore: score,
                  details: d.details ?? {}
                } as CategoryScore;
              } else {
              // Convert number to CategoryScore
              processedCategoryScores[category] = {
                score,
                maxScore: 10,
                displayName: defaultDisplayName,
                reasoning: '',
                originalScore: score
              } as CategoryScore;
              }
            } else {
              // Support both camelCase and snake_case keys from backend
              const s: any = score;
              processedCategoryScores[category] = {
                score: s.score ?? 0,
                maxScore: s.maxScore ?? s.max_score ?? 10,
                displayName: s.displayName ?? s.display_name ?? defaultDisplayName,
                reasoning: s.reasoning ?? s.details?.reasoning ?? s.details?.analysis ?? s.analysis ?? '',
                originalScore: s.originalScore ?? s.score ?? 0,
                details: s.details ?? {}
              } as CategoryScore;
            }
          });
        }
        
        // Final fallback: ensure reasoning present by checking full_analysis
        const detailedCatScores: any = (result as any).full_analysis?.category_scores || {};
        Object.entries(processedCategoryScores).forEach(([cat, cs]) => {
          if (!cs.reasoning && detailedCatScores[cat] && typeof detailedCatScores[cat] === 'object') {
            const d = detailedCatScores[cat] as any;
            cs.reasoning = d.reasoning || d.analysis || d.comment || d.notes || '';
            cs.maxScore = cs.maxScore || d.maxScore || d.max_score || 10;
            cs.displayName = cs.displayName || d.displayName || d.display_name || cat.replace(/_/g, ' ').replace(/^\w/, c=>c.toUpperCase());
          }
        });
        // Create the processed result with properly typed category scores
        const processedResult: AnalysisResult = {
          ...result,
          category_scores: processedCategoryScores,
          // Ensure all required fields have defaults
          key_strengths: result.key_strengths || [],
          key_weaknesses: result.key_weaknesses || [],
          recommendations: result.recommendations || [],
          recommendations_by_category: result.recommendations_by_category || {},
          next_steps: result.next_steps || [],
          original_filename: result.original_filename || '',
          file_size_bytes: result.file_size_bytes || 0
        };
        
        setParsedResult(processedResult);
        setErrorState(null);
      } catch (err) {
        console.error('Error processing result:', err);
        setErrorState('Failed to process analysis results');
        setParsedResult(null);
      } finally {
        setIsLoadingState(false);
      }
    };

    processData();
  }, [result]);

  // Handle loading, error, or missing result states
  if (isLoadingState || errorState || !parsedResult) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {isLoadingState && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {errorState && (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {errorState}
          </Alert>
        )}
        {!isLoadingState && !errorState && (
          <Alert severity="info">
            No analysis results available. Please upload a pitch deck to analyze.
          </Alert>
        )}
      </Container>
    );
  }

  // Default analysis object
  const analysis = {
    overall_score: parsedResult.overall_score || 0,
    category_scores: parsedResult.category_scores || {},
    key_strengths: parsedResult.key_strengths || [],
    key_weaknesses: parsedResult.key_weaknesses || [],
    recommendations: parsedResult.recommendations || [],
    overall_analysis: parsedResult.overall_analysis || parsedResult.analysis || 'No analysis available.',
    analysis_timestamp: parsedResult.analysis_timestamp || new Date().toISOString(),
    model_used: parsedResult.model_used || 'unknown'
  };

  return (
    <Paper elevation={3} className="printable-report" sx={{ p: 3, my: 4, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Pitch Deck Analysis</Typography>
        <Box>
          <Chip
            label={`Overall Score: ${getScoreLabel(analysis.overall_score, 'outOfTen')}`}
            color={getScoreColor(analysis.overall_score * 10, 'percentage')}
            sx={{ fontSize: '1rem', fontWeight: 'bold', mr: 1 }}
          />
          <Button onClick={() => window.print()} variant="contained" size="small" className="no-print" sx={{ mr: 1 }}>
            Download Report
          </Button>
          {sample ? (
            <Button href="/analyze" variant="outlined" size="small" className="no-print">
              Analyze Your Deck
            </Button>
          ) : (
            <Button onClick={clearResult} variant="outlined" size="small" className="no-print">
              Clear Results
            </Button>
          )}
        </Box>
      </Box>

      {typeof parsedResult.score_percentile === 'number' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          This deck scored higher than {parsedResult.score_percentile}% of pitch decks we've analyzed.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />
      
      {/* Overall Analysis */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>Overall Analysis</Typography>
        <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
          {analysis.overall_analysis ? (
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
              {analysis.overall_analysis}
            </Typography>
          ) : (
            <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No analysis available. Please try analyzing your pitch deck again.
            </Box>
          )}
        </Paper>
      </Box>

      {/* Category Scores */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>Category Scores</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {Object.entries(analysis.category_scores).map(([category, scoreData]) => {
            // Safely extract score and maxScore, handling both number and CategoryScore types
            const scoreValue = typeof scoreData === 'number' ? scoreData : (scoreData?.score || 0);
            const maxScore = (typeof scoreData === 'object' && scoreData?.maxScore) || 10;
            const normalizedScore = (scoreValue / maxScore) * 10; // Convert to 0-10 scale
            
            // Get display name, handling both number and CategoryScore types
            const displayName = typeof scoreData === 'object' 
              ? (scoreData.displayName || formatCategoryName(category.replace(/_/g, ' ')))
              : formatCategoryName(category.replace(/_/g, ' '));
              
            // Get reasoning if available, fallback to details.reasoning
            const reasoning = typeof scoreData === 'object' ? (scoreData.reasoning || (scoreData.details as any)?.reasoning || (scoreData.details as any)?.analysis || (scoreData as any).analysis || '') : '';
            
            return (
              <Paper key={category} variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
                    {displayName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {scoreValue.toFixed(1)}/{maxScore}
                    </Typography>
                    <Chip 
                      label={getScoreLabel(normalizedScore, 'outOfTen')}
                      size="small"
                      color={getScoreColor(normalizedScore * 10, 'percentage')}
                      sx={{ minWidth: 60 }}
                    />
                  </Box>
                </Box>
                <ScoreBar score={scoreValue} max={maxScore} showScore={false} />
                {reasoning && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    {reasoning}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Key Strengths */}
      {analysis.key_strengths?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>Key Strengths</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body1">
              {analysis.key_strengths.join(' • ')}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Key Weaknesses */}
      {analysis.key_weaknesses?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>Key Weaknesses</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body1">
              {analysis.key_weaknesses.join(' • ')}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>Recommendations</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body1">
              {analysis.recommendations.join(' • ')}
            </Typography>
          </Paper>
        </Box>
      )}

      <Box sx={{ mt: 4, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          Analysis performed on {new Date(analysis.analysis_timestamp).toLocaleString()} using {analysis.model_used}
        </Typography>
      </Box>
    </Paper>
  );
};
